/**
 * Line diff between two Mermaid sources — compares the current buffer
 * against a chosen `plotter:history` row (history.ts's `HistoryRow`), one
 * of the two features SPEC.md §13 originally deferred ("Diff view between
 * two versions of a diagram").
 *
 * Classic LCS (longest common subsequence) over lines, same shape as `diff`/
 * `git diff`'s own algorithm — no new dependency (CLAUDE.md rule 3):
 * Mermaid sources are at most a few hundred lines, so the O(n*m) table this
 * builds is trivial in practice. Pure — no DOM/localStorage access — so it's
 * unit-testable on its own, mirroring history.ts's split between pure logic
 * and thin impure wrappers.
 */

export type DiffLineKind = 'same' | 'add' | 'remove';

export interface DiffLine {
  kind: DiffLineKind;
  text: string;
}

/**
 * `before`/`after` split on `\n` — a trailing newline produces one trailing
 * empty-string line on both sides, which the LCS table treats like any
 * other line (it'll match as `same` when both sides have one, same as any
 * other identical pair).
 */
export function diffLines(before: string, after: string): DiffLine[] {
  const a = before.split('\n');
  const b = after.split('\n');
  const n = a.length;
  const m = b.length;

  // lcs[i][j] = length of the LCS of a[i:] and b[j:]. One extra row/column
  // of zeros at the far edge is the base case (either side exhausted).
  const lcs: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i]![j] = a[i] === b[j] ? lcs[i + 1]![j + 1]! + 1 : Math.max(lcs[i + 1]![j]!, lcs[i]![j + 1]!);
    }
  }

  // Walk the table front-to-back, preferring a match, then whichever branch
  // the table says is longer — the standard reconstruction, tie-broken
  // toward "remove before add" so an edited line reads as a removal
  // immediately followed by its replacement, not interleaved.
  const result: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      result.push({ kind: 'same', text: a[i]! });
      i++;
      j++;
    } else if (lcs[i + 1]![j]! >= lcs[i]![j + 1]!) {
      result.push({ kind: 'remove', text: a[i]! });
      i++;
    } else {
      result.push({ kind: 'add', text: b[j]! });
      j++;
    }
  }
  while (i < n) {
    result.push({ kind: 'remove', text: a[i]! });
    i++;
  }
  while (j < m) {
    result.push({ kind: 'add', text: b[j]! });
    j++;
  }
  return result;
}

/** True when a diff has at least one add or remove line — an all-`same` diff means the two sources are identical. */
export function hasChanges(lines: DiffLine[]): boolean {
  return lines.some((line) => line.kind !== 'same');
}
